<template>
  <div class="ball-stage" :style="containerStyle">
    <!-- "Show whole image" mode: a blurred, cropped copy fills the bars left by
         the letterboxed image, so any aspect ratio looks intentional -->
    <div v-if="backgroundImage && backgroundFit === 'contain'" class="stage-image stage-image-backdrop" :style="imageStyle('cover')"></div>
    <div v-if="backgroundImage" class="stage-image" :style="imageStyle(backgroundFit)"></div>
    <div class="ball" :style="ballStyle"></div>
  </div>
</template>

<script>
// Shared visual renderer for the EMDR ball animation.
// Used by both the patient view and the therapist view so that what the
// therapist sees on screen is pixel-for-pixel identical to the patient,
// instead of a separately-simulated preview.
//
// Emits `turn` ({ side, at }) with the upcoming moment the ball turns back at
// the left or right edge (top/bottom in the vertical pattern), and `tempo`
// with the time between turns, so bilateral sound can land on the turns.
// Times are performance.now() milliseconds.

// `speed` is in pixels per 60 Hz frame; movement is scaled by the real time
// between frames so it's the same on high refresh rate screens
const FRAME_MS = 1000 / 60;
// Longest step after a stalled or hidden frame, so the ball doesn't jump
const MAX_STEP_MS = 100;
// A frame is on screen about one frame after it's computed
const DISPLAY_DELAY_MS = FRAME_MS;

export default {
  name: 'BallStage',
  props: {
    speed: { type: Number, default: 5 },
    bounceMode: { type: String, default: 'horizontal' },
    isMoving: { type: Boolean, default: false },
    ballSize: { type: Number, default: 30 },
    backgroundColor: { type: String, default: '#ffffff' },
    backgroundImage: { type: String, default: '' },
    backgroundFit: { type: String, default: 'cover' },
    ballColor: { type: String, default: '#2196f3' },
    randomColor: { type: Boolean, default: false }
  },
  data() {
    return {
      positionX: 0,
      positionY: 0,
      directionX: 1,
      directionY: 1,
      animationFrame: null,
      figure8Time: 0,
      lastBounceMode: this.bounceMode,
      displayColor: this.ballColor,
      colorChangeInterval: null,
      stageWidth: 0,
      stageHeight: 0
    };
  },
  computed: {
    ballStyle() {
      return {
        left: this.positionX + 'px',
        top: this.positionY + 'px',
        background: this.displayColor,
        width: this.ballSize + 'px',
        height: this.ballSize + 'px',
        borderRadius: '50%',
        position: 'absolute',
        willChange: this.isMoving ? 'transform' : 'auto',
        transform: 'translate3d(0, 0, 0)'
      };
    },
    containerStyle() {
      return {
        background: this.backgroundColor
      };
    },
    // Time from one turn to the next at the current speed, in ms
    turnPeriod() {
      const pxPerMs = this.speed / FRAME_MS;
      if (!(pxPerMs > 0)) return 0;
      if (this.bounceMode === 'figure8') {
        return Math.PI / (pxPerMs * 0.01);
      }
      const range = this.bounceMode === 'vertical'
        ? this.stageHeight - this.ballSize
        : this.stageWidth - this.ballSize;
      return Math.max(range, 1) / pxPerMs;
    }
  },
  watch: {
    randomColor(active) {
      if (active) {
        this.startRandomColor();
      } else {
        this.stopRandomColor();
        this.displayColor = this.ballColor;
      }
    },
    ballColor(newColor) {
      if (!this.randomColor) {
        this.displayColor = newColor;
      }
    },
    turnPeriod(ms) {
      if (ms > 0) {
        this.$emit('tempo', ms);
      }
    }
  },
  created() {
    // Per-frame bookkeeping, kept out of data() so it isn't made reactive
    this.lastFrameTime = 0;
    this.lastTurn = null;
  },
  mounted() {
    const width = this.$el.clientWidth;
    const height = this.$el.clientHeight;
    this.positionX = Math.floor(width / 2 - this.ballSize / 2);
    this.positionY = Math.floor(height / 2 - this.ballSize / 2);
    if (this.randomColor) {
      this.startRandomColor();
    }
    this.measure();
    window.addEventListener('resize', this.measure);
    this.animationFrame = requestAnimationFrame(this.animate);
  },
  beforeDestroy() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    window.removeEventListener('resize', this.measure);
    this.stopRandomColor();
  },
  methods: {
    imageStyle(fit) {
      return {
        backgroundImage: `url("${this.backgroundImage}")`,
        backgroundSize: fit === 'contain' ? 'contain' : 'cover'
      };
    },
    measure() {
      this.stageWidth = this.$el.clientWidth;
      this.stageHeight = this.$el.clientHeight;
    },
    // `dt` is the elapsed time in 60 Hz frames
    moveBall(dt) {
      const width = this.$el.clientWidth;
      const height = this.$el.clientHeight;
      const minX = 0;
      const minY = 0;
      const maxX = width - this.ballSize;
      const maxY = height - this.ballSize;

      if (this.bounceMode !== this.lastBounceMode) {
        this.positionX = Math.floor(width / 2 - this.ballSize / 2);
        this.positionY = Math.floor(height / 2 - this.ballSize / 2);
        this.lastBounceMode = this.bounceMode;
      }

      if (this.bounceMode === 'horizontal') {
        this.positionY = Math.floor(height / 2 - this.ballSize / 2);
        this.positionX += this.speed * dt * this.directionX;
        if (this.positionX >= maxX) {
          this.positionX = maxX;
          this.directionX = -1;
        } else if (this.positionX <= minX) {
          this.positionX = minX;
          this.directionX = 1;
        }
      } else if (this.bounceMode === 'vertical') {
        this.positionX = Math.floor(width / 2 - this.ballSize / 2);
        this.positionY += this.speed * dt * this.directionY;
        if (this.positionY >= maxY) {
          this.positionY = maxY;
          this.directionY = -1;
        } else if (this.positionY <= minY) {
          this.positionY = minY;
          this.directionY = 1;
        }
      } else if (this.bounceMode === 'diagonal') {
        this.positionX += this.speed * dt * this.directionX;
        this.positionY += this.speed * dt * this.directionY;
        if (this.positionX >= maxX) {
          this.positionX = maxX;
          this.directionX = -1;
        } else if (this.positionX <= minX) {
          this.positionX = minX;
          this.directionX = 1;
        }
        if (this.positionY >= maxY) {
          this.positionY = maxY;
          this.directionY = -1;
        } else if (this.positionY <= minY) {
          this.positionY = minY;
          this.directionY = 1;
        }
      } else if (this.bounceMode === 'random') {
        this.positionX += this.speed * dt * this.directionX;
        this.positionY += this.speed * dt * this.directionY;
        if (this.positionX >= maxX || this.positionX <= minX) {
          this.directionX = Math.random() > 0.5 ? 1 : -1;
        }
        if (this.positionY >= maxY || this.positionY <= minY) {
          this.directionY = Math.random() > 0.5 ? 1 : -1;
        }
        this.positionX = Math.max(minX, Math.min(maxX, this.positionX));
        this.positionY = Math.max(minY, Math.min(maxY, this.positionY));
      } else if (this.bounceMode === 'figure8') {
        const centerX = width / 2;
        const centerY = height / 2;
        const scale = Math.min(width, height) * 0.35;

        this.figure8Time += this.speed * 0.01 * dt;

        const t = this.figure8Time;
        this.positionX = centerX + scale * Math.sin(t) - this.ballSize / 2;
        this.positionY = centerY + scale * Math.sin(t) * Math.cos(t) - this.ballSize / 2;

        this.positionX = Math.max(minX, Math.min(maxX, this.positionX));
        this.positionY = Math.max(minY, Math.min(maxY, this.positionY));
      }
    },
    // How long until the ball turns back, and at which side
    nextTurn() {
      const pxPerMs = this.speed / FRAME_MS;
      if (!(pxPerMs > 0)) return null;
      const maxX = this.$el.clientWidth - this.ballSize;
      const maxY = this.$el.clientHeight - this.ballSize;
      switch (this.bounceMode) {
        case 'horizontal':
        case 'diagonal':
        case 'random':
          return this.directionX > 0
            ? { side: 'right', ms: Math.max(0, maxX - this.positionX) / pxPerMs }
            : { side: 'left', ms: Math.max(0, this.positionX) / pxPerMs };
        case 'vertical':
          // No left/right here, so the sound alternates top = left, bottom = right
          return this.directionY > 0
            ? { side: 'right', ms: Math.max(0, maxY - this.positionY) / pxPerMs }
            : { side: 'left', ms: Math.max(0, this.positionY) / pxPerMs };
        case 'figure8': {
          // x follows sin(t), which turns at t = (k + 1/2)π: right for even k
          const k = Math.floor(this.figure8Time / Math.PI - 0.5) + 1;
          const ms = ((k + 0.5) * Math.PI - this.figure8Time) / (pxPerMs * 0.01);
          return { side: k % 2 === 0 ? 'right' : 'left', ms };
        }
        default:
          return null;
      }
    },
    trackTurn(now) {
      const turn = this.nextTurn();
      if (!turn) return;
      const at = now + turn.ms + DISPLAY_DELAY_MS;
      const last = this.lastTurn;
      // Report each turn once, and again if its timing shifts before the
      // ball gets there (speed or window changed, ball paused, tab hidden)
      if (last && last.side === turn.side && (turn.ms === 0 || Math.abs(at - last.at) < 4)) {
        return;
      }
      this.lastTurn = { side: turn.side, at };
      this.$emit('turn', { side: turn.side, at });
    },
    animate(now) {
      const elapsed = this.lastFrameTime ? Math.min(now - this.lastFrameTime, MAX_STEP_MS) : FRAME_MS;
      this.lastFrameTime = now;
      if (this.isMoving) {
        this.moveBall(elapsed / FRAME_MS);
        this.trackTurn(now);
      }
      this.animationFrame = requestAnimationFrame(this.animate);
    },
    startRandomColor() {
      if (this.colorChangeInterval) return;
      this.colorChangeInterval = setInterval(() => {
        this.displayColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
      }, 200);
    },
    stopRandomColor() {
      if (this.colorChangeInterval) {
        clearInterval(this.colorChangeInterval);
        this.colorChangeInterval = null;
      }
    }
  }
};
</script>

<style scoped>
.ball-stage {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}
.stage-image {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-position: center;
  background-repeat: no-repeat;
}
.stage-image-backdrop {
  top: -40px;
  right: -40px;
  bottom: -40px;
  left: -40px;
  filter: blur(28px) brightness(0.85);
}
.ball {
  position: absolute;
  z-index: 1;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  -webkit-perspective: 1000;
  perspective: 1000;
}
</style>
